/**
 * "System Admin" page — `/admin`.
 *
 * Renders both system-admin surfaces on the SAME page, side by side
 * on wide viewports and stacked on narrow ones:
 *
 *   - Dashboard Users   — accounts that can sign into the dashboard
 *                         (invite, role, per-user overrides, delete).
 *   - Roles & Privileges — what each role grants (editable matrix on
 *                         top of the baseline in
 *                         `packages/shared/src/permissions.ts`).
 *
 * Nothing CRM-side lives here anymore — CRM user / tenant management
 * is a CRM concern. The previous tabbed shell with five panels has
 * been collapsed into one focused page so an admin can see who has
 * which role and what that role grants at a glance.
 *
 * Backwards-compat: `/admin/dashboard-users` and `/admin/roles` still
 * exist as deep-link URLs that just <Navigate> to `/admin`. The old
 * `/admin/crm-*` and `/admin/tenants` URLs redirect here too.
 */
import { Box, Typography } from "@mui/material";

import { DashboardUsersPanel } from "./DashboardUsersPanel.js";
import { RolesPanel } from "./RolesPanel.js";

/** Small all-caps section heading above each panel. */
const sectionHeadingSx = {
  mb: 1.5,
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.disabled",
} as const;

/**
 * "User Management" page — the `/admin/users` child of AdminLayout.
 * The shared "System Admin" header + tab strip live in
 * `AdminLayout.tsx`; this component renders just the two panels so it
 * sits cleanly beside the new "Products & Releases" page.
 */
export function SystemAdminLayout(): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xl: "repeat(2, 1fr)" },
          alignItems: { xl: "start" },
        }}
      >
        <Box component="section" aria-labelledby="admin-users-heading" sx={{ minWidth: 0 }}>
          <Typography component="h2" id="admin-users-heading" sx={sectionHeadingSx}>
            Dashboard Users
          </Typography>
          <DashboardUsersPanel />
        </Box>

        <Box component="section" aria-labelledby="admin-roles-heading" sx={{ minWidth: 0 }}>
          <Typography component="h2" id="admin-roles-heading" sx={sectionHeadingSx}>
            Roles &amp; Privileges
          </Typography>
          <RolesPanel />
        </Box>
      </Box>
    </Box>
  );
}
